import moment from 'moment';
import {
  CustomerType,
  IraAccountType,
  JointAccountType,
  Relationship,
  DOB_BE_FORMAT,
  DOB_FE_FORMAT,
  E_SIGNED_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  INCOME_OPTIONS,
  INVESTMENT_EXPERIENCE_OPTIONS,
  INVESTMENT_OBJECTIVE_OPTIONS,
  IS_POLITICALLY_EXPOSED_OPTIONS,
  LIQUIDITY_NEEDS_OPTIONS,
  NET_WORTH_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  STATES,
  TIME_HORIZON_OPTIONS,
  YES_NO_OPTIONS,
  APEX_STATUS,
  RELATIONSHIP_OPTIONS,
  INVESTMENT_TYPE_OPTS,
  GENERAL_ACCOUNT_TYPE_OPTS,
  RETIREMENT_ACCOUNT_TYPE_OPTS,
} from './ApexConstants';
import {
  DraftAccount,
  SelectOption,
  FormFields,
  ApplicantFormFields,
  RetirementAccountBeneficiaryDraft,
  RetirementAccountBeneficiaryFormField,
  Applicant,
} from './ApexTypes';

export const isApexAccountOpen = status => {
  return APEX_STATUS.indexOf(status) === -1;
};

const getIncomeOption = (
  valueMin: number | null,
  valueDefault: number | null,
  options: SelectOption<{ min: number; max: number }>[],
): SelectOption<{ min: number; max: number }> => {
  // If we have a min value set already (e.g. annualIncomeUSDMin) ➜ get the corresponding option
  // If not, use the default value (from the BE)
  //
  // (This is will change. Income as well as Liquid and Total NetWorth will be regular inputs soon)
  if (typeof valueMin === 'number' || typeof valueDefault === 'number') {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const value = typeof valueMin === 'number' ? valueMin : valueDefault!;

    for (let i = 0; i < options.length; i += 1) {
      const {
        value: { min, max },
      } = options[i];

      if (value >= min && value <= max) {
        return options[i];
      }
    }
  }

  return options[0];
};

interface AccountSelectionFields {
  investmentType: SelectOption<number>;
  accountType: SelectOption<number>;
}
const getAccountSelectionFields = (draftAccount: DraftAccount): AccountSelectionFields => {
  const { customerType, accountIraType, jointAccountType } = draftAccount;

  const defaultAccountSelectionFields = {
    investmentType: INVESTMENT_TYPE_OPTS[0],
    accountType: GENERAL_ACCOUNT_TYPE_OPTS[0],
  };

  switch (customerType) {
    case CustomerType.Individual:
      return defaultAccountSelectionFields;
    case CustomerType.Ira:
      switch (accountIraType) {
        case IraAccountType.TraditionalIra:
          return {
            investmentType: INVESTMENT_TYPE_OPTS[1],
            accountType: RETIREMENT_ACCOUNT_TYPE_OPTS[0],
          };
        case IraAccountType.RolloverIra:
          return {
            investmentType: INVESTMENT_TYPE_OPTS[1],
            accountType: RETIREMENT_ACCOUNT_TYPE_OPTS[1],
          };
        case IraAccountType.RothIra:
          return {
            investmentType: INVESTMENT_TYPE_OPTS[1],
            accountType: RETIREMENT_ACCOUNT_TYPE_OPTS[2],
          };
        case IraAccountType.SimpleIraParticipant:
          return {
            investmentType: INVESTMENT_TYPE_OPTS[1],
            accountType: RETIREMENT_ACCOUNT_TYPE_OPTS[3],
          };
        default:
          return defaultAccountSelectionFields;
      }
    case CustomerType.Joint:
      if (jointAccountType === JointAccountType.JointRightsOfSurvivorship) {
        return {
          investmentType: INVESTMENT_TYPE_OPTS[0],
          accountType: GENERAL_ACCOUNT_TYPE_OPTS[1],
        };
      }
      return defaultAccountSelectionFields;

    default:
      return defaultAccountSelectionFields;
  }
};

const parseRetirementAccountBeneficiaryForFE: (
  values: RetirementAccountBeneficiaryDraft | null,
) => RetirementAccountBeneficiaryFormField = values => {
  if (values) {
    const {
      familyName,
      givenName,
      dateOfBirth,
      relationship,
      streetAddress,
      city,
      postalCode,
      state,
      socialSecurityNumber,
    } = values;

    const getRelationship: (option: SelectOption<Relationship>) => boolean = option =>
      relationship === option.value;

    return {
      familyName: familyName || '',
      givenName: givenName || '',
      streetAddress: streetAddress || '',
      city: city || '',
      postalCode: postalCode || '',
      socialSecurityNumber: socialSecurityNumber || '',
      dateOfBirth: moment(dateOfBirth, DOB_BE_FORMAT).format(DOB_FE_FORMAT) || '',
      relationship:
        (RELATIONSHIP_OPTIONS.find(getRelationship) as SelectOption<Relationship>) || null,
      state: (STATES.find(({ value }) => state === value) as SelectOption<string>) || null,
    };
  }

  return {
    familyName: '',
    givenName: '',
    streetAddress: '',
    city: '',
    postalCode: '',
    socialSecurityNumber: '',
    dateOfBirth: '',
    relationship: null,
    state: null,
  };
};

const getApplicantFormFields = (applicant: Applicant): ApplicantFormFields => ({
  ...applicant,
  legalFirstName: applicant.legalFirstName || '',
  legalLastName: applicant.legalLastName || '',
  email: applicant.email || '',
  streetAddressLine1: applicant.streetAddressLine1 || '',
  city: applicant.city || '',
  state: STATES.find(({ value }) => applicant.state === value) || null,
  postalCode: applicant.postalCode || '',
  dob: applicant.dob ? moment(applicant.dob, DOB_BE_FORMAT).format(DOB_FE_FORMAT) : '',
  ssn: applicant.ssn || '',
  phoneNumber: applicant.phoneNumber || '',
  employmentStatus:
    EMPLOYMENT_STATUS_OPTIONS.find(({ value }) => applicant.employmentStatus === value) ||
    EMPLOYMENT_STATUS_OPTIONS[0],
  employerName: applicant.employerName || '',
  positionEmployed: applicant.positionEmployed || '',
});

/**
 * Converts the BE data structure for an Apex draft account into a format ready to feed Formik
 *
 * @param draftAccount: DraftAccount
 * @returns FormFields
 */
export const draftAccountToFormFields = (draftAccount: DraftAccount): FormFields | null => {
  if (!draftAccount) {
    return null;
  }

  const { investmentType, accountType } = getAccountSelectionFields(draftAccount);

  return {
    ...draftAccount,

    primaryApplicant: getApplicantFormFields(draftAccount.primaryApplicant),
    secondApplicant: getApplicantFormFields(draftAccount.secondApplicant),

    accountType,
    investmentType,

    annualIncome: getIncomeOption(
      draftAccount.annualIncomeUSDMin,
      draftAccount.annualIncomeUSDDefault,
      INCOME_OPTIONS,
    ),
    liquidNetWorth: getIncomeOption(
      draftAccount.liquidNetWorthUSDMin,
      draftAccount.liquidNetWorthUSDDefault,
      NET_WORTH_OPTIONS,
    ),
    totalNetWorth: getIncomeOption(
      draftAccount.totalNetWorthUSDMin,
      draftAccount.totalNetWorthUSDDefault,
      NET_WORTH_OPTIONS,
    ),

    isControlPerson: YES_NO_OPTIONS[draftAccount.isControlPerson ? 0 : 1],

    isCustodianEmployee: YES_NO_OPTIONS[draftAccount.isCustodianEmployee ? 0 : 1],

    companySymbols: draftAccount.companySymbols ? draftAccount.companySymbols[0] : '',

    isAffiliatedExchangeOrFINRA: YES_NO_OPTIONS[draftAccount.isAffiliatedExchangeOrFINRA ? 0 : 1],

    brokerDealerfirmName: draftAccount.brokerDealerfirmName || '',

    snapId:
      draftAccount.snapIds && draftAccount.snapIds.length > 0 ? draftAccount.snapIds[0] : null,

    isPoliticallyExposed:
      IS_POLITICALLY_EXPOSED_OPTIONS.find(
        ({ value }) => draftAccount.isPoliticallyExposed === value,
      ) || IS_POLITICALLY_EXPOSED_OPTIONS[1],
    immediateFamily: draftAccount.immediateFamily || '',
    politicalOrganization: draftAccount.politicalOrganization || '',

    liquidityNeeds:
      LIQUIDITY_NEEDS_OPTIONS.find(({ value }) => draftAccount.liquidityNeeds === value) ||
      LIQUIDITY_NEEDS_OPTIONS[1],

    timeHorizon:
      TIME_HORIZON_OPTIONS.find(({ value }) => draftAccount.timeHorizon === value) ||
      TIME_HORIZON_OPTIONS[1],

    riskTolerance:
      RISK_TOLERANCE_OPTIONS.find(({ value }) => draftAccount.riskTolerance === value) || null,

    investmentExperience:
      INVESTMENT_EXPERIENCE_OPTIONS.find(
        ({ value }) => draftAccount.investmentExperience === value,
      ) || null,

    investmentObjective:
      INVESTMENT_OBJECTIVE_OPTIONS.find(
        ({ value }) => draftAccount.investmentObjective === value,
      ) || INVESTMENT_OBJECTIVE_OPTIONS[0],

    hasTrustedContact: YES_NO_OPTIONS[draftAccount.trustedContactInfo ? 0 : 1],

    trustedContactFirstName:
      draftAccount.trustedContactInfo && draftAccount.trustedContactInfo.legalFirstName
        ? draftAccount.trustedContactInfo.legalFirstName
        : '',

    trustedContactLastName:
      draftAccount.trustedContactInfo && draftAccount.trustedContactInfo.legalLastName
        ? draftAccount.trustedContactInfo.legalLastName
        : '',

    trustedContactEmail:
      draftAccount.trustedContactInfo && draftAccount.trustedContactInfo.email
        ? draftAccount.trustedContactInfo.email
        : '',

    hasInterestedParty: YES_NO_OPTIONS[draftAccount.interestedPartyInfo ? 0 : 1],

    interestedPartyFirstName:
      draftAccount.interestedPartyInfo && draftAccount.interestedPartyInfo.legalFirstName
        ? draftAccount.interestedPartyInfo.legalFirstName
        : '',

    interestedPartyLastName:
      draftAccount.interestedPartyInfo && draftAccount.interestedPartyInfo.legalLastName
        ? draftAccount.interestedPartyInfo.legalLastName
        : '',

    interestedPartyStreetAddress:
      draftAccount.interestedPartyInfo && draftAccount.interestedPartyInfo.streetAddress
        ? draftAccount.interestedPartyInfo.streetAddress
        : '',

    interestedPartyCity:
      draftAccount.interestedPartyInfo && draftAccount.interestedPartyInfo.city
        ? draftAccount.interestedPartyInfo.city
        : '',

    interestedPartyState:
      STATES.find(
        state =>
          state.value ===
          (draftAccount.interestedPartyInfo ? draftAccount.interestedPartyInfo.state : null),
      ) || null,

    interestedPartyPostalCode:
      draftAccount.interestedPartyInfo && draftAccount.interestedPartyInfo.postalCode
        ? draftAccount.interestedPartyInfo.postalCode
        : '',

    eSigned:
      E_SIGNED_OPTIONS.find(({ value }) => draftAccount.eSigned === value) || E_SIGNED_OPTIONS[0],

    hasBeneficiary: YES_NO_OPTIONS[draftAccount.retirementAccountBeneficiaryInfo ? 0 : 1],

    retirementAccountBeneficiaryInfo: parseRetirementAccountBeneficiaryForFE(
      draftAccount.retirementAccountBeneficiaryInfo,
    ),

    simpleIraPlanName: draftAccount.simpleIraPlanName || '',
  };
};
